// Ephemeral runtime status for live terminal connections, keyed by session id.
// NEVER persisted — the heavy handles (xterm.Terminal, WebSocket) live in
// terminalManager; this store only mirrors the reactive bits panes render on,
// plus `lastActivityAt` for a future idle sweeper.

import { create } from 'zustand';

export type ConnStatus =
    | 'connecting'
    | 'ready'
    | 'disconnected'
    | 'closed'
    | 'error';

export interface RuntimeConn {
    status: ConnStatus;
    error?: string;
    cols: number;
    rows: number;
    lastActivityAt: number;
}

// A paste held back by the multiline-paste guard, awaiting user confirmation
// in PasteWarningDialog.
export interface PendingPaste {
    sessionId: string;
    text: string;
}

interface RuntimeStore {
    conns: Record<string, RuntimeConn>;
    // Session whose pane is showing the find-in-terminal bar (null = closed).
    searchFor: string | null;
    pendingPaste: PendingPaste | null;
    setStatus: (sessionId: string, status: ConnStatus, error?: string) => void;
    setSize: (sessionId: string, cols: number, rows: number) => void;
    touch: (sessionId: string) => void;
    remove: (sessionId: string) => void;
    openSearch: (sessionId: string) => void;
    closeSearch: () => void;
    setPendingPaste: (paste: PendingPaste) => void;
    clearPendingPaste: () => void;
}

const blank = (): RuntimeConn => ({
    status: 'connecting',
    cols: 0,
    rows: 0,
    lastActivityAt: Date.now(),
});

export const useTerminalRuntime = create<RuntimeStore>((set) => ({
    conns: {},
    searchFor: null,
    pendingPaste: null,
    setStatus: (sessionId, status, error) =>
        set((s) => ({
            conns: {
                ...s.conns,
                [sessionId]: {
                    ...(s.conns[sessionId] ?? blank()),
                    status,
                    error: status === 'error' ? error : undefined,
                },
            },
        })),
    setSize: (sessionId, cols, rows) =>
        set((s) => ({
            conns: {
                ...s.conns,
                [sessionId]: { ...(s.conns[sessionId] ?? blank()), cols, rows },
            },
        })),
    touch: (sessionId) =>
        set((s) => {
            const cur = s.conns[sessionId];
            if (!cur) return s;
            return {
                conns: {
                    ...s.conns,
                    [sessionId]: { ...cur, lastActivityAt: Date.now() },
                },
            };
        }),
    remove: (sessionId) =>
        set((s) => {
            if (
                !(sessionId in s.conns) &&
                s.searchFor !== sessionId &&
                s.pendingPaste?.sessionId !== sessionId
            ) {
                return s;
            }
            const next = { ...s.conns };
            delete next[sessionId];
            return {
                conns: next,
                searchFor: s.searchFor === sessionId ? null : s.searchFor,
                pendingPaste:
                    s.pendingPaste?.sessionId === sessionId
                        ? null
                        : s.pendingPaste,
            };
        }),
    openSearch: (sessionId) => set({ searchFor: sessionId }),
    closeSearch: () => set({ searchFor: null }),
    setPendingPaste: (paste) => set({ pendingPaste: paste }),
    clearPendingPaste: () => set({ pendingPaste: null }),
}));
